import {z} from 'zod';

/**
 * Identifiers are branded, so the compiler refuses an organization id where a user id
 * belongs. Both are uuids at runtime and the brand exists only at compile time — nothing
 * is added to the emitted JavaScript, and nothing changes on the wire.
 *
 * The asymmetry is the point. A branded id *is* a string, so it can be handed to Drizzle,
 * interpolated into a path or written to a log line unchanged. Going the other way needs
 * a parse, which is exactly where an id enters the system anyway: a path parameter, a
 * request body, or a database row on its way through a mapper.
 *
 * In a multi-tenant tool the organization id is the isolation boundary, so mixing the two
 * up is the mistake with the highest cost and the least chance of being noticed.
 */
export const userIdSchema = z.uuid().brand<'UserId'>();

export type UserId = z.infer<typeof userIdSchema>;

export const organizationIdSchema = z.uuid().brand<'OrganizationId'>();

export type OrganizationId = z.infer<typeof organizationIdSchema>;

/**
 * A congestion area belongs to exactly one organization and is what a request is made
 * against. `request` carries three id columns and `openadr_message` two, which is the
 * shape where an unbranded string lets two of them be swapped unnoticed.
 */
export const congestionAreaIdSchema = z.uuid().brand<'CongestionAreaId'>();

export type CongestionAreaId = z.infer<typeof congestionAreaIdSchema>;

/**
 * A cluster of congestion areas, which is what an MW ceiling is held against. Named as
 * ElaadNL name it, and deliberately not "group": a resource group and a flex group
 * already mean something else here. Branded for the same reason an area is: `congestion_area`
 * now carries both ids, and an unbranded string lets the two be swapped unnoticed.
 */
export const congestionAreaClusterIdSchema = z.uuid().brand<'CongestionAreaClusterId'>();

export type CongestionAreaClusterId = z.infer<typeof congestionAreaClusterIdSchema>;

export const requestIdSchema = z.uuid().brand<'RequestId'>();

export type RequestId = z.infer<typeof requestIdSchema>;

export const openadrMessageIdSchema = z.uuid().brand<'OpenadrMessageId'>();

export type OpenadrMessageId = z.infer<typeof openadrMessageIdSchema>;

/**
 * The key of a server-side preview, not a table id — and the one identifier a caller
 * hands back to us, which is why it is branded like the rest. Submit consumes it by
 * inserting the request (ADR 0013), so it reaches the database as a column value.
 */
export const previewIdSchema = z.uuid().brand<'PreviewId'>();

export type PreviewId = z.infer<typeof previewIdSchema>;

/**
 * One wizard run. Every request the same submit created carries it, which is what lets the
 * list show them as one row (`domain/request.ts`). Generated here, never supplied by a
 * caller: it names a thing the server did, not a thing the client knows about.
 */
export const submissionIdSchema = z.uuid().brand<'SubmissionId'>();

export type SubmissionId = z.infer<typeof submissionIdSchema>;

/**
 * For a value that is already known to be an id of this kind — a row that a repository
 * has just read back, or a test fixture. Skips validation, so it is only ever correct
 * where the uuid has already been proven.
 */
export const asUserId = (value: string): UserId => value as UserId;

export const asOrganizationId = (value: string): OrganizationId => value as OrganizationId;

export const asCongestionAreaId = (value: string): CongestionAreaId => value as CongestionAreaId;

export const asCongestionAreaClusterId = (value: string): CongestionAreaClusterId => value as CongestionAreaClusterId;

export const asRequestId = (value: string): RequestId => value as RequestId;

export const asOpenadrMessageId = (value: string): OpenadrMessageId => value as OpenadrMessageId;

export const asPreviewId = (value: string): PreviewId => value as PreviewId;

export const asSubmissionId = (value: string): SubmissionId => value as SubmissionId;
